<template>
  <div v-if="showMask" :class="ns.e('mask')">
    <svg style="width: 100%; height: 100%">
      <defs>
        <mask :id="'ws-tour-v2-mask-' + maskId">
          <rect
            fill="white"
            :x="maskOuter.x"
            :y="maskOuter.y"
            :width="maskOuter.width"
            :height="maskOuter.height"
          />
          <rect
            v-if="showPlaceholder"
            :class="{
              [ns.e('mask-placeholder-animated')]: placeholderAnimated,
            }"
            fill="black"
            rx="2"
            :x="maskInner.x"
            :y="maskInner.y"
            :width="maskInner.width"
            :height="maskInner.height"
          />
        </mask>
      </defs>
      <rect
        :fill="maskColor"
        :x="maskOuter.x"
        :y="maskOuter.x"
        :width="maskOuter.width"
        :height="maskOuter.height"
        :mask="'url(#ws-tour-v2-mask-' + maskId + ')'"
      />
      <rect
        fill="transparent"
        pointer-events="auto"
        :x="maskLeft.x"
        :y="maskLeft.y"
        :width="maskLeft.width"
        :height="maskLeft.height"
      />
      <rect
        v-if="showPlaceholder"
        fill="transparent"
        pointer-events="auto"
        :x="maskRight.x"
        :y="maskRight.y"
        :width="maskRight.width"
        :height="maskRight.height"
      />
      <rect
        v-if="showPlaceholder"
        fill="transparent"
        pointer-events="auto"
        :x="maskTop.x"
        :y="maskTop.y"
        :width="maskTop.width"
        :height="maskTop.height"
      />
      <rect
        v-if="showPlaceholder"
        fill="transparent"
        pointer-events="auto"
        :x="maskBottom.x"
        :y="maskBottom.y"
        :width="maskBottom.width"
        :height="maskBottom.height"
      />
    </svg>
  </div>
</template>
<script lang="ts">
import { defineComponent } from 'vue'
import { useNamespace } from '@element-plus/hooks'

let tourMaskIn = 0
const DEFAULT_MASK = 'rgba(0,0,0,0.5)'

export type Rect = {
  x?: number
  y?: number
  width?: number | string
  height?: number | string
}

const defaultRect: Rect = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
}

export default defineComponent({
  name: 'ElTourV2Mask',
  expose: ['update', 'maskInner'],

  props: {
    x: {
      type: Number,
      default: 0,
    },
    y: {
      type: Number,
      default: 0,
    },
    width: {
      type: Number,
      default: 0,
    },
    height: {
      type: Number,
      default: 0,
    },
    offset: {
      type: Number,
      default: 8,
    },
    mask: {
      type: [Boolean, String],
      default: DEFAULT_MASK,
    },
  },

  data() {
    const ns = useNamespace('tour-v2')
    return {
      ns,
      maskId: ++tourMaskIn,
      maskOuter: {
        x: 0,
        y: 0,
        width: '100vw',
        height: '100vh',
      },
      placeholderAnimated: false,
      maskInner: { ...defaultRect },
      maskTop: { ...defaultRect },
      maskBottom: { ...defaultRect },
      maskLeft: { ...defaultRect },
      maskRight: { ...defaultRect },
    }
  },

  computed: {
    hasPlaceholder() {
      return this.width && this.height
    },

    showMask() {
      return !(typeof this.mask == 'boolean' && !this.mask)
    },

    showPlaceholder() {
      return this.showMask && this.hasPlaceholder
    },

    maskColor(): string {
      return typeof this.mask === 'string'
        ? (this.mask as string)
        : DEFAULT_MASK
    },
  },

  watch: {
    showPlaceholder: {
      handler(val) {
        if (val) {
          setTimeout(() => {
            // TO 缓存过度效果 - 从左上角过渡到目标位置
            this.placeholderAnimated = true
          }, 1)
        } else {
          this.placeholderAnimated = false
        }
      },
      immediate: true,
    },
  },

  methods: {
    update() {
      this.maskInner = this.getMaskInner()
      this.maskTop = this.getMaskTop()
      this.maskBottom = this.getMaskBottom()
      this.maskLeft = this.getMaskLeft()
      this.maskRight = this.getMaskRight()
    },

    getMaskInner(): Rect {
      if (this.hasPlaceholder) {
        return {
          x: this.x - this.offset,
          y: this.y - this.offset,
          width: this.width + 2 * this.offset,
          height: this.height + 2 * this.offset,
        }
      }
      return {}
    },

    getMaskTop(): Rect {
      if (this.hasPlaceholder) {
        return {
          x: 0,
          y: 0,
          width: '100vw',
          height: `${Math.max(0, this.y - this.offset)}px`,
        }
      }
      return {}
    },

    getMaskBottom(): Rect {
      if (this.hasPlaceholder) {
        const y = this.y + this.height + this.offset
        return {
          x: 0,
          y,
          width: '100vw',
          height: `calc(100vh - ${Math.max(0, y)}px`,
        }
      }
      return {}
    },

    getMaskLeft(): Rect {
      if (this.hasPlaceholder) {
        return {
          x: 0,
          y: 0,
          width: `${Math.max(0, this.x - this.offset)}px`,
          height: '100vh',
        }
      }
      return {
        x: 0,
        y: 0,
        width: '100vw',
        height: '100vh',
      }
    },

    getMaskRight(): Rect {
      if (this.hasPlaceholder) {
        const x = this.x + this.width + this.offset
        return {
          x,
          y: 0,
          width: `calc(100vw - ${Math.max(0, x)}px)`,
          height: '100vh',
        }
      }
      return {}
    },
  },
})
</script>
